import {
  DEFAULT_BRANCH_ICON_URL,
  formatMapOrderLabel,
  hasCustomBranchImage,
} from "utils/mapMarkerUtils"

const RED_PIN_SVG = `<svg width="24" height="32" viewBox="0 0 28 36" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
  <path d="M14 0C6.27 0 0 6.27 0 14c0 10.5 14 22 14 22s14-11.5 14-22C28 7.16 21.73 0 14 0z" fill="#EA4335"/>
  <circle cx="14" cy="14" r="5.5" fill="#fff"/>
</svg>`

let OrdersMapMarkerOverlayClass = null

const getOverlayClass = () => {
  if (OrdersMapMarkerOverlayClass || !window.google?.maps) {
    return OrdersMapMarkerOverlayClass
  }

  OrdersMapMarkerOverlayClass = class extends window.google.maps.OverlayView {
    constructor({ position, type, label, imageUrl, onClick }) {
      super()
      this.position = position
      this.type = type
      this.label = label || ""
      this.imageUrl = imageUrl || ""
      this.onClick = onClick
      this.container = null
    }

    onAdd() {
      this.container = document.createElement("div")
      this.container.className = `orders-map-marker orders-map-marker--${this.type}`
      this.container.style.position = "absolute"
      this.container.style.cursor = "pointer"
      this.container.style.transform = "translate(-50%, -100%)"
      this.container.style.zIndex = this.type === "branch" ? "200" : "20"

      if (this.type === "order") {
        const labelEl = document.createElement("div")
        labelEl.className = "orders-map-marker__label orders-map-marker__label--order"
        labelEl.textContent = formatMapOrderLabel(this.label)
        labelEl.title = this.label
        this.container.appendChild(labelEl)

        const pinEl = document.createElement("div")
        pinEl.className = "orders-map-marker__pin"
        pinEl.innerHTML = RED_PIN_SVG
        this.container.appendChild(pinEl)
      } else {
        const useBranchImage = hasCustomBranchImage(this.imageUrl)
        const iconEl = document.createElement("div")
        iconEl.className = useBranchImage
          ? "orders-map-marker__shop-wrap orders-map-marker__shop-wrap--image"
          : "orders-map-marker__shop-wrap orders-map-marker__shop-wrap--default"
        const shopEl = document.createElement("div")
        shopEl.className = useBranchImage
          ? "orders-map-marker__shop orders-map-marker__shop--image"
          : "orders-map-marker__shop orders-map-marker__shop--default"
        const img = document.createElement("img")
        img.alt = this.label
        img.src = useBranchImage ? this.imageUrl : DEFAULT_BRANCH_ICON_URL
        img.className = useBranchImage
          ? "orders-map-marker__shop-image"
          : "orders-map-marker__shop-default-icon"
        shopEl.appendChild(img)
        iconEl.appendChild(shopEl)
        this.container.appendChild(iconEl)

        const labelEl = document.createElement("div")
        labelEl.className = "orders-map-marker__label orders-map-marker__label--branch"
        labelEl.textContent = this.label
        this.container.appendChild(labelEl)
      }

      this.container.addEventListener("click", event => {
        event.stopPropagation()
        this.onClick?.()
      })

      this.getPanes().overlayMouseTarget.appendChild(this.container)
    }

    draw() {
      if (!this.container) return
      const projection = this.getProjection()
      if (!projection) return

      const point = projection.fromLatLngToDivPixel(
        new window.google.maps.LatLng(this.position.lat, this.position.lng)
      )
      if (!point) return

      this.container.style.transform = "translate(-50%, -100%)"
      this.container.style.top =
        this.type === "branch" ? `${point.y - 10}px` : `${point.y}px`
      this.container.style.left = `${point.x}px`
    }

    onRemove() {
      if (this.container?.parentNode) {
        this.container.parentNode.removeChild(this.container)
      }
      this.container = null
    }
  }

  return OrdersMapMarkerOverlayClass
}

export const clearOrdersMapOverlays = overlays => {
  ;(overlays || []).forEach(overlay => overlay?.setMap(null))
}

export const createOrdersMapOverlays = ({
  map,
  branchMarkers = [],
  orderMarkers = [],
  onOrderMarkerClick,
  onBranchMarkerClick,
}) => {
  const OverlayClass = getOverlayClass()
  if (!OverlayClass || !map) return []

  const overlays = []

  orderMarkers.forEach(marker => {
    const overlay = new OverlayClass({
      position: marker.displayPosition || marker.position,
      type: "order",
      label: marker.orderNo ? String(marker.orderNo) : "",
      onClick: () => onOrderMarkerClick?.(marker),
    })
    overlay.setMap(map)
    overlays.push(overlay)
  })

  branchMarkers.forEach(marker => {
    const overlay = new OverlayClass({
      position: marker.position,
      type: "branch",
      label: marker.name,
      imageUrl: marker.imageUrl,
      onClick: () => onBranchMarkerClick?.(marker),
    })
    overlay.setMap(map)
    overlays.push(overlay)
  })

  return overlays
}

export const refreshOrdersMapOverlays = overlays => {
  ;(overlays || []).forEach(overlay => {
    if (typeof overlay?.draw === "function") {
      overlay.draw()
    }
  })
}

export const fitOrdersMapBounds = ({
  map,
  branchMarkers = [],
  orderMarkers = [],
  focusBranchIds = [],
  padding = 72,
}) => {
  if (!map || !window.google?.maps) return

  const focusSet = new Set((focusBranchIds || []).map(String))
  const focusedBranches = focusSet.size
    ? branchMarkers.filter(marker => focusSet.has(String(marker.id)))
    : []

  const bounds = new window.google.maps.LatLngBounds()
  const pointsToFit = []

  const addPoint = position => {
    if (!position) return
    bounds.extend(position)
    pointsToFit.push(position)
  }

  if (focusedBranches.length > 0) {
    focusedBranches.forEach(marker => addPoint(marker.position))
    orderMarkers
      .filter(marker => {
        const branchRef = marker.order?.branchId
        const branchId =
          typeof branchRef === "object" ? branchRef?._id : branchRef
        return branchId && focusSet.has(String(branchId))
      })
      .forEach(marker =>
        addPoint(marker.displayPosition || marker.position)
      )
  } else {
    branchMarkers.forEach(marker => addPoint(marker.position))
    orderMarkers.forEach(marker =>
      addPoint(marker.displayPosition || marker.position)
    )
  }

  if (!pointsToFit.length) return

  // fitBounds fails on a single point — center on branch(es) even with zero orders.
  if (pointsToFit.length === 1) {
    map.setCenter(pointsToFit[0])
    map.setZoom(15)
    return
  }

  map.fitBounds(bounds, padding)

  if (focusedBranches.length === 1 && orderMarkers.length === 0) {
    window.google.maps.event.addListenerOnce(map, "idle", () => {
      const zoom = map.getZoom()
      if (zoom > 15) map.setZoom(15)
    })
  }
}
